import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Modal from '../Global/Modals/Modal';
import OutlinedSelectInput from '../Global/Inputs/OutlinedSelect';
import TextFieldInput from '../Global/Inputs/TextField';
import ChipFieldInput from '../Global/Inputs/ChipField';
import CheckboxInput from '../Global/Inputs/Checkbox';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import { Formik, Field } from 'formik';
import ProgressButton from '../Global/Buttons/Progress';
import Grid from '@material-ui/core/Grid';
import GridItem from '../Global/GridItem';
import HelpIcon from '@material-ui/icons/Help';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import ButtonStyles from '../../styles/button';
import SpacingStyles from '../../styles/helpers/spacing';

import ShipmentReferenceModel from '../../Models/ShipmentReference';
import ShipmentReferenceSchema from './ShipmentReference.schema';
import ShipmentReferenceFormStyles from './__styles__/ShipmentReferenceForm.styles';

import { AppContext } from '../../AppProvider';

export const TYPES = {
  TEXT: 'TEXT',
  DROP_DOWN: 'DROP_DOWN',
  COMBO: 'COMBO'
};

const CUSTOM_FIELD_REQUIRED_VALUE = 'ERROR';
const CUSTOM_FIELD_NO_REQUIRED_VALUE = 'NONE';

const CUSTOM_FIELD_VISIBILITY_VALUE = 'SHOW';
const CUSTOM_FIELD_HIDDEN_VALUE = 'SELECT';

export class ShipmentReferenceForm extends Component {
  static propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    shipmentReference: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      shipmentReference: { ...ShipmentReferenceModel() }
    };
  }

  componentDidMount() {
    const {
      referenceData: { customFieldTypes }
    } = this.context.globalProps;
    this.setState({
      customFieldTypes: customFieldTypes
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.shipmentReference !== prevProps.shipmentReference) {
      this.setState({
        shipmentReference: { ...this.props.shipmentReference }
      });
    }
  }
  _handleOnClose = () => {
    this.props.onClose();
  };

  _handleOnSave = async shipmentReference => {
    const { onSave } = this.props;
    try {
      await onSave(shipmentReference);
      return true;
    } catch {
      return false;
    }
  };

  _handleSetRequired = ({ target: { name, value } }, setFieldValue) => {
    setFieldValue(name, value);
    if (value === CUSTOM_FIELD_REQUIRED_VALUE) {
      setFieldValue('customFieldVisibilityId', CUSTOM_FIELD_VISIBILITY_VALUE);
    } else {
      setFieldValue('customFieldVisibilityId', CUSTOM_FIELD_HIDDEN_VALUE);
    }
  };

  render() {
    const { classes, open, t, isSubmitting } = this.props;
    const shipmentReference = this.props.shipmentReference;
    return (
      <Formik
        validationSchema={ShipmentReferenceSchema({ t })}
        enableReinitialize={true}
        initialValues={shipmentReference}
        onSubmit={async (values, actions) => {
          const saved = await this._handleOnSave(values);
          if (saved) {
            this._handleOnClose();
            actions.resetForm();
          }
        }}
        onReset={(_values, actions) => {
          actions.resetForm();
          this._handleOnClose();
        }}
        validateOnChange={false}
        validateOnBlur={false}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          dirty,
          setFieldValue
        }) => (
          <Modal
            open={open}
            onClose={this._handleOnClose}
            classes={{
              footer: classes.modalFooter
            }}
            body={
              <Fragment>
                <div className={classes.titleWrapper}>
                  <p className={classnames(classes.headline6, classes.title)}>
                    {`${
                      values.isNew
                        ? t('Add Shipment Reference Field')
                        : t('Edit Shipment Reference Field')
                    }`}
                  </p>
                </div>
                <Grid container spacing={16}>
                  <GridItem xs={12}>
                    <Field
                      name="label"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.label}
                            label={t('Reference Label')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6} className={classes.placeholderDisplay}>
                    <Field
                      name="description"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={values.description}
                            label={t('Placeholder Text')}
                            fullWidth
                            field={field}
                            form={form}
                            endAdornment={
                              <HelpIcon className={classes.placeholderIcon} />
                            }
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <Field
                      name={'customFieldTypeId'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('Field Type')}
                            value={values.customFieldTypeId}
                            options={this.state.customFieldTypes}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  {values.customFieldTypeId &&
                    values.customFieldTypeId !== TYPES.TEXT && (
                      <GridItem xs={12}>
                        <Field
                          name="listItems"
                          render={({ field, form }) => (
                            <ChipFieldInput
                              label={t('Values')}
                              field={field}
                              form={form}
                              onChange={handleChange}
                              value={[...values.listItems] || []}
                            />
                          )}
                        />
                      </GridItem>
                    )}
                  <div className={classes.checkboxesContainer}>
                    <Fragment>
                      <Field
                        name="customFieldRequirementLevelId"
                        render={({ field, form }) => (
                          <CheckboxInput
                            label={
                              <p className={classes.checkboxLabel}>
                                <strong>{t('Require Field')}</strong>
                                <br />
                                {t('A value is required to create a shipment.')}
                              </p>
                            }
                            values={{
                              true: CUSTOM_FIELD_REQUIRED_VALUE,
                              false: CUSTOM_FIELD_NO_REQUIRED_VALUE
                            }}
                            field={field}
                            form={form}
                            classes={{
                              root: classes.checkbox
                            }}
                            onChange={evt =>
                              this._handleSetRequired(evt, setFieldValue)
                            }
                          />
                        )}
                      />
                      <Field
                        name="customFieldVisibilityId"
                        onChange={handleChange}
                        render={({ field, form }) => (
                          <CheckboxInput
                            label={
                              <p className={classes.checkboxLabel}>
                                <strong>{t('Display Field')}</strong>
                                <br />
                                {t(
                                  'Field is automatically provided, but a value is not required.'
                                )}
                              </p>
                            }
                            values={{
                              true: CUSTOM_FIELD_VISIBILITY_VALUE,
                              false: CUSTOM_FIELD_HIDDEN_VALUE
                            }}
                            checkedValue={'SHOW'}
                            uncheckedValue={'SELECT'}
                            field={field}
                            form={form}
                            classes={{
                              root: classes.checkbox
                            }}
                            disabled={
                              values.customFieldRequirementLevelId ===
                              CUSTOM_FIELD_REQUIRED_VALUE
                            }
                          />
                        )}
                      />
                    </Fragment>
                  </div>
                </Grid>
              </Fragment>
            }
            footer={
              <Fragment>
                <div className={classes.spacer} />
                <Button
                  className={classes.button}
                  onClick={handleReset}
                  color={'primary'}
                  classes={{ label: classes.buttonLabel }}
                >
                  {t('Cancel')}
                </Button>
                <ProgressButton
                  onClick={handleSubmit}
                  className={classes.button}
                  classes={{
                    disabled: classes.buttonDisabled,
                    label: classes.buttonLabel
                  }}
                  color={'primary'}
                  disabled={!dirty || isSubmitting}
                  isSubmitting={isSubmitting}
                >
                  {values.id ? t('Save') : t('Add')}
                </ProgressButton>
              </Fragment>
            }
          />
        )}
      />
    );
  }
}

ShipmentReferenceForm.contextType = AppContext;
const translatedShipmentReferenceForm = withNamespaces()(ShipmentReferenceForm);
export default withStyles(
  combineStyles(
    ShipmentReferenceFormStyles,
    LayoutStyles,
    TypographyStyles,
    ButtonStyles,
    SpacingStyles
  )
)(translatedShipmentReferenceForm);
